import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Battery, AlertTriangle, Package, PlusCircle, Database, Activity, Zap, CheckCircle2, XCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell as RechartsCell, Tooltip } from "recharts";


export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/api/dashboard/stats`)
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
        <div className="w-6 h-6 border-2 border-neon-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats) return null;

  const chartData = [
    { name: "Neuve", value: stats.cells_neuve, color: "#A855F7" },
    { name: "Tres bon", value: stats.cells_tres_bon, color: "#06B6D4" },
    { name: "Bon", value: stats.cells_bon, color: "#22C55E" },
    { name: "A surveiller", value: stats.cells_surveiller, color: "#EAB308" },
    { name: "Defectueuse", value: stats.cells_defectueuse, color: "#EF4444" },
  ];

  const capacityData = (stats.capacity_distribution || []).map((b) => ({ name: b.range, value: b.count }));

  const statCards = [
    { label: "Total cellules", value: stats.total_cells, icon: Battery, color: "text-white", glow: "" },
    { label: "Neuves", value: stats.cells_neuve ?? 0, icon: Zap, color: "text-neon-purple", glow: "neon-text-purple" },
    { label: "Tres bonnes", value: stats.cells_tres_bon ?? 0, icon: Zap, color: "text-neon-cyan", glow: "neon-text-cyan" },
    { label: "Bonnes", value: stats.cells_bon, icon: Zap, color: "text-neon-green", glow: "neon-text-cyan" },
    { label: "A surveiller", value: stats.cells_surveiller, icon: AlertTriangle, color: "text-neon-yellow", glow: "" },
    { label: "Defectueuses", value: stats.cells_defectueuse, icon: AlertTriangle, color: "text-neon-red", glow: "" },
    { label: "Disponibles", value: stats.cells_disponible ?? 0, icon: CheckCircle2, color: "text-neon-blue", glow: "neon-text-blue" },
    { label: "Indisponibles", value: stats.cells_indisponible ?? 0, icon: XCircle, color: "text-muted-foreground", glow: "" },
    { label: "Packs actifs", value: stats.total_packs, icon: Package, color: "text-neon-cyan", glow: "neon-text-cyan" },
    { label: "Series", value: stats.series_list?.length || 0, icon: Database, color: "text-neon-purple", glow: "neon-text-purple" },
  ];

  return (
    <div className="space-y-6 pb-20 sm:pb-0" data-testid="dashboard-page">
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => navigate("/cells/create")}
          className="bg-gradient-to-r from-neon-blue to-neon-purple text-white font-bold uppercase tracking-wide hover:from-neon-blue-hover hover:to-neon-purple rounded-lg h-11 neon-glow-blue"
          data-testid="dashboard-create-cells-btn"
        >
          <PlusCircle className="w-4 h-4 mr-2" />
          Creer un lot de cellules
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/library")}
          className="border-neon-blue/20 hover:border-neon-blue hover:text-neon-blue rounded-lg h-11 bg-transparent"
          data-testid="dashboard-library-btn"
        >
          <Database className="w-4 h-4 mr-2" />
          Bibliotheque
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate("/live")}
          className="border-neon-cyan/20 hover:border-neon-cyan hover:text-neon-cyan rounded-lg h-11 bg-transparent"
          data-testid="dashboard-live-btn"
        >
          <Activity className="w-4 h-4 mr-2" />
          Mesure en direct
        </Button>
      </div>

      {/* Floating Action Button (mobile) */}
      <button
        onClick={() => navigate("/cells/create")}
        className="fixed bottom-20 right-4 z-40 sm:hidden w-14 h-14 bg-gradient-to-br from-neon-blue to-neon-purple rounded-full flex items-center justify-center neon-glow-blue hover:scale-105 active:scale-95 transition-all"
        data-testid="fab-create-cells"
      >
        <PlusCircle className="w-6 h-6 text-white" />
      </button>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4" data-testid="dashboard-stats-grid">
        {statCards.map(({ label, value, icon: Icon, color, glow }) => (
          <Card key={label} className="bg-cyber-surface border-neon-blue/10 rounded-xl hover:border-neon-blue/30 transition-all duration-300 hover:neon-glow-blue group">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${color} group-hover:drop-shadow-[0_0_4px_currentColor] transition-all`} />
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
              </div>
              <p className={`font-mono text-3xl font-bold ${color} ${glow}`} data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
                {value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart */}
        <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl" data-testid="dashboard-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Etat des cellules</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.total_cells > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} barSize={40}>
                  <XAxis dataKey="name" tick={{ fill: "#6B7994", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#4B5E7A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#111d32", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", fontFamily: "JetBrains Mono", fontSize: "12px" }}
                    labelStyle={{ color: "#FFFFFF" }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chartData.map((entry, i) => (
                      <RechartsCell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm font-mono">
                Aucune cellule enregistree
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Batches */}
        <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl" data-testid="dashboard-recent-batches">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Derniers lots crees</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recent_batches?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_batches.map((batch) => (
                  <div
                    key={batch.id}
                    className="flex items-center justify-between p-3 bg-cyber-bg/50 border border-neon-blue/10 rounded-lg hover:border-neon-blue/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono text-xs border-neon-blue/30 text-neon-blue px-2">
                        {batch.series}
                      </Badge>
                      <span className="text-sm text-white font-mono">{batch.count} cellules</span>
                    </div>
                    <span className="text-xs text-muted-foreground font-mono">
                      {new Date(batch.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm font-mono">
                Aucun lot cree
              </div>
            )}
          </CardContent>
        </Card>

        {/* Capacity distribution */}
        <Card className="bg-cyber-surface border-neon-blue/10 rounded-xl lg:col-span-2" data-testid="dashboard-capacity-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base text-white">Distribution par capacite (mAh)</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.total_cells > 0 && capacityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={capacityData} barSize={48}>
                  <XAxis dataKey="name" tick={{ fill: "#6B7994", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#4B5E7A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#111d32", border: "1px solid rgba(59, 130, 246, 0.2)", borderRadius: "8px", fontFamily: "JetBrains Mono", fontSize: "12px" }}
                    labelStyle={{ color: "#FFFFFF" }}
                    formatter={(v) => [v, "cellules"]}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[240px] text-muted-foreground text-sm font-mono">
                Aucune donnee de capacite
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
